import { useState, useRef, useEffect } from "react"
import { CaptchaElement } from "shared/src/components/CaptchaElement/CaptchaElement"
import { SignUpCardWithImage } from "shared/src/components/SignUpComponents/SignUpCardWithImage"
import { SignUpForm } from "shared/src/components/SignUpComponents/SignUpForm/SignUpForm/SignUpForm"
import "./ForgotPassword.css"
import { Auth } from "@aws-amplify/auth"
import { onError } from "shared/src/libs/errorLib/errorLib"
import { useQuery } from "shared/src/libs/queryLib/queryLib"
import { validatePassword } from "shared/src/libs/validationLib/validationLib"
import { useFormFields } from "shared/src/libs/hooksLib/hooksLib"
import { useLogin, Response } from "shared/src/libs/loginLib/loginLib"
import ReCAPTCHA from "react-google-recaptcha"
import { Spinner } from "@shadcn/components/ui/spinner"
import { Alert, AlertDescription, AlertTitle } from "@shadcn/components/ui/alert"
import { Info } from "lucide-react"
import { MFAValidateInner } from "shared/src/components/MFA/MFA"

export function ForgotPassword() {
	const captchaRef = useRef<ReCAPTCHA>(null)
	const query = useQuery()
	const code = query.get("x-code") || ""
	const email = query.get("email") || ""
	const [isLoading, setIsLoading] = useState(false)
	const [emailNeedsFilling, setEmailNeedsFilling] = useState(false)
	const [codeResent, setCodeResent] = useState<"sending" | "sent" | "not sending">("not sending")
	const performLogin = useLogin({ responseOnly: false })
	const validationRef = useRef(null)
	const mostRecentSigninResponse = useRef<Response & { challengeName?: string }>(null)
	const [fields, handleFieldChange, updateFields] = useFormFields({
		email: email,
		code: code,
		mfaCode: "",
		newPassword: "",
		confirmNewPassword: "",
	})
	const [currentStage, setCurrentStage] = useState<"sendCode" | "enterCode" | "mfaValidate">(
		code === "" ? "sendCode" : "enterCode"
	)

	useEffect(() => {
		let timeoutId: NodeJS.Timeout
		if (codeResent === "sent") {
			timeoutId = setTimeout(() => {
				setCodeResent("not sending")
			}, 10000)
		}
		return () => clearTimeout(timeoutId)
	}, [codeResent])

	function validateForm() {
		return fields.email.length > 0
	}

	function validateConfirmationForm() {
		return (
			fields.email.length > 0 &&
			fields.code.length > 0 &&
			validatePassword(fields.newPassword) &&
			fields.newPassword === fields.confirmNewPassword
		)
	}

	async function handleSubmit(event) {
		event.preventDefault()
		setIsLoading(true)

		await Auth.forgotPassword(fields.email)
		setIsLoading(false)
		setCurrentStage("enterCode")
	}

	async function onCaptchaChange(validationData) {
		validationRef.current = validationData
		await submitCode(validationRef.current)
	}

	async function submitAfterMFACode() {
		try {
			if (!validationRef.current.token) {
				throw new Error("Captcha failed")
			}
			await performLogin({ email: fields.email, password: fields.newPassword, validationData: validationRef.current })
		} catch (e) {
			onError(e)
			captchaRef.current.reset()
			setIsLoading(false)
		}
	}

	async function submitCode(validationData) {
		try {
			if (!validationData.token) {
				throw new Error("Captcha failed")
			}
			validationRef.current = validationData
			await Auth.forgotPasswordSubmit(fields.email, fields.code, fields.newPassword)
			mostRecentSigninResponse.current = await Auth.signIn(fields.email, fields.newPassword, validationRef.current)
			if (mostRecentSigninResponse.current.challengeName === "SOFTWARE_TOKEN_MFA") {
				setCurrentStage("mfaValidate")
			} else {
				await performLogin({ email: fields.email, password: fields.newPassword, validationData: validationRef.current })
			}
		} catch (e) {
			onError(e)
			captchaRef.current.reset()
			setIsLoading(false)
		}
	}

	async function resendCode() {
		if (fields.email.length > 0) {
			try {
				setCodeResent("sending")
				await Auth.forgotPassword(fields.email)
				setCodeResent("sent")
				setEmailNeedsFilling(false)
				updateFields({ code: "" })
			} catch (e) {
				setCodeResent("not sending")
				onError(e)
			}
		} else {
			setEmailNeedsFilling(true)
		}
	}

	async function handleConfirmationSubmit(event) {
		event.preventDefault()
		setIsLoading(true)
		captchaRef.current.execute()
	}

	function renderCurrentPage() {
		switch (currentStage) {
			case "sendCode":
				return (
					<div className="forgot-password-form-container-1">
						<SignUpForm
							title={
								<>
									<h1 className="bs-h1">Forgot Password?</h1>
									<h3 className="bs-h3">No worries, we'll email you instructions to reset your password</h3>
								</>
							}
							button={{
								text: "Send email",
								role: "submit",
								onClick: handleSubmit,
								disabled: !validateForm(),
								isLoading: isLoading,
							}}
							fields={[
								{
									label: "Email",
									type: "email",
									placeholder: "Enter email",
									fieldName: "email",
									initialValue: fields.email,
									autofocus: true,
									autocomplete: "email",
									required: true,
									inputFieldHandler: handleFieldChange,
								},
							]}
						/>
					</div>
				)
			case "enterCode":
				return (
					<div className="forgot-password-form-container-2">
						<SignUpForm
							title={
								<>
									<h1 className="bs-h1">Reset Password</h1>
									<h3 className="bs-h3">Enter the code from your email and choose a new password</h3>
									{/* Can't be shown only to SSO users: revealing which emails are SSO before sign-in would let anyone probe for accounts. */}
									<Alert variant="info" role="note" className="mt-2">
										<Info />
										<div>
											<AlertTitle>Sign in with Access Identity?</AlertTitle>
											<AlertDescription className="text-sm">
												Your email has a <strong>Continue</strong> button instead of a code. Use it to set or reset your
												password, and ignore this form.
											</AlertDescription>
										</div>
									</Alert>
								</>
							}
							button={{
								text: "Reset Password",
								role: "submit",
								onClick: handleConfirmationSubmit,
								disabled: !validateConfirmationForm(),
								isLoading: isLoading,
							}}
							fields={[
								{
									label: "Email",
									type: "email",
									placeholder: "Enter email",
									fieldName: "email",
									initialValue: fields.email,
									disabled: email.length > 0 && fields.email === email,
									autocomplete: "username",
									required: true,
									inputFieldHandler: handleFieldChange,
									description: (
										<span
											style={
												emailNeedsFilling && fields.email.length === 0
													? { color: "var(--ara-error)" }
													: { display: "none" }
											}>
											Fill in the email to resend the code.
										</span>
									),
								},
								{
									label: "Reset Code",
									type: "text",
									inputMode: "numeric",
									placeholder: "Enter reset code",
									fieldName: "code",
									autocomplete: "one-time-code",
									required: true,
									disabled: code.length > 0 && fields.code === code,
									initialValue: fields.code,
									inputFieldHandler: handleFieldChange,
									description: (
										<>
											<span>Please check your inbox/spam</span>
											{codeResent !== "sent" ? (
												<>
													<span> or </span>
													<span style={{ textDecoration: "underline", cursor: "pointer" }} onClick={resendCode}>
														Resend the code {codeResent === "sending" && <Spinner className="inline size-4" />}
													</span>
												</>
											) : (
												<>
													<span>. </span>
													<span style={{ color: "var(--ara-success)" }}>Code resent successfully</span>
												</>
											)}
										</>
									),
								},
								{
									label: "New Password",
									type: "password",
									placeholder: "Create password",
									fieldName: "newPassword",
									autocomplete: "new-password",
									required: true,
									passwordAlert: true,
									initialValue: fields.newPassword,
									inputFieldHandler: handleFieldChange,
								},
								{
									label: "Confirm New password",
									type: "password",
									placeholder: "Confirm password",
									fieldName: "confirmNewPassword",
									autocomplete: "new-password",
									required: true,
									initialValue: fields.confirmNewPassword,
									inputFieldHandler: handleFieldChange,
									description: (
										<span
											style={
												fields.newPassword === fields.confirmNewPassword
													? { display: "none" }
													: { color: "var(--ara-error)" }
											}>
											Password & Confirm Password do not match.
										</span>
									),
								},
							]}
						/>
						<div>
							<CaptchaElement ref={captchaRef} onChange={onCaptchaChange} shouldExecuteAfterErrorReset={isLoading} />
						</div>
					</div>
				)
			case "mfaValidate":
				return (
					<MFAValidateInner
						signinResponse={mostRecentSigninResponse.current}
						handleFieldChange={handleFieldChange}
						fields={fields}
						onSubmit={submitAfterMFACode}
					/>
				)
			default:
				return <div> </div>
		}
	}

	return <SignUpCardWithImage size="medium">{renderCurrentPage()}</SignUpCardWithImage>
}
